import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, FileCheck2, Check, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { newKey } from "@/lib/utils";
import { inr, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { HorizontalStepper, buildSteps } from "@/components/Stepper";
import { FieldInput } from "@/components/FieldInput";
import { FormFields, compact, FORM_DIALOG } from "@/components/RecordDialog";
import { DetailGrid } from "@/components/PageHeader";
import { PrintButton } from "@/components/PrintButton";

const AWARD_FIELDS = [
  { section: "Contract details", name: "site", label: "Site", type: "select", required: true, options: ["My Home Avali", "My Home Akrida", "Tellapur Technocity"] },
  { section: "Contract details", name: "package", label: "Package", required: true },
  { section: "Contract details", name: "contract_type", label: "Contract Type", type: "select", options: ["Item Rate", "Lump Sum", "Labour Only", "EPC"] },
  { section: "Contract details", name: "scope", label: "Scope of Work", type: "textarea" },
  { section: "Schedule & commercial terms", name: "start_date", label: "Start Date", type: "date" },
  { section: "Schedule & commercial terms", name: "completion_date", label: "Completion Date", type: "date" },
  { section: "Schedule & commercial terms", name: "payment_terms", label: "Payment Terms", type: "select", options: ["Advance", "15 days", "30 days", "45 days", "60 days", "Milestone based"] },
  { section: "Schedule & commercial terms", name: "mobilisation_advance_pct", label: "Mobilisation Advance (%)", type: "number" },
  { section: "Schedule & commercial terms", name: "retention_pct", label: "Retention (%)", type: "number" },
  { section: "Schedule & commercial terms", name: "defect_liability_months", label: "Defect Liability (months)", type: "number" },
];

const SITES = ["My Home Avali", "My Home Akrida", "Tellapur Technocity"];
const CHECKS = [
  ["vendor_docs", "Vendor registration & pre-qualification docs"],
  ["compliance_certs", "Compliance & statutory certificates"],
  ["approval_note", "Signed approval note"],
  ["wo_draft", "Work Order draft"],
];

const NewAwardDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({ bids: [] });
  const [tenders, setTenders] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF({ bids: [{ _k: newKey(), vendor: "", amount: "" }] });
    api.get("/m/comparative_statements").then((r) => setTenders([...new Set(r.data.map((c) => c.tender_ref))]));
  }, [open]);
  const setBid = (i, k, v) => setF((s) => ({ ...s, bids: s.bids.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const save = async () => {
    try {
      const bids = f.tender_ref ? [] : f.bids.filter((b) => b.vendor && b.amount).map((b) => ({ vendor: b.vendor, amount: Number(b.amount) }));
      const { bids: _b, tender_ref: _t, ...rest } = compact(f);
      const { data } = await api.post("/contract-awards", { ...rest, tender_ref: f.tender_ref || "", bids });
      toast.success(`${data.ref_no} created`);
      onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-award" className={FORM_DIALOG}>
        <DialogHeader><DialogTitle className="font-heading">New contract award</DialogTitle></DialogHeader>
        <FormFields fields={AWARD_FIELDS} form={f} setForm={setF} />
        <div className="space-y-1.5">
          <Label>Import bids from Comparative Statement</Label>
          <Select value={f.tender_ref || "none"} onValueChange={(v) => setF((s) => ({ ...s, tender_ref: v === "none" ? "" : v }))}>
            <SelectTrigger data-testid="select-award-tender"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Enter bids manually</SelectItem>
              {tenders.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!f.tender_ref && <>
        <Label>Bids received</Label>
        {f.bids.map((b, i) => (
          <div key={b._k} className="grid grid-cols-[1fr_140px_auto] gap-2">
            <FieldInput field={{ name: `bid-vendor-${i}`, type: "ref", ref: "vendors" }} value={b.vendor} onChange={(v) => setBid(i, "vendor", v)} />
            <Input type="number" placeholder="Quoted ₹" value={b.amount} onChange={(e) => setBid(i, "amount", e.target.value)} data-testid={`input-bid-amount-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setF((s) => ({ ...s, bids: s.bids.filter((x) => x._k !== b._k) }))} data-testid={`btn-remove-bid-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setF((s) => ({ ...s, bids: [...s.bids, { _k: newKey(), vendor: "", amount: "" }] }))} data-testid="btn-add-bid"><Plus className="mr-1 h-3.5 w-3.5" />Add bid</Button>
        </>}
        <DialogFooter><Button onClick={save} data-testid="btn-create-award">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CSTable = ({ c, editable, onUpdate }) => {
  const sorted = [...c.bids].sort((a, b) => a.amount - b.amount);
  return (
    <Panel title="Comparative statement summary" testId="cs-summary">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{["Rank", "Vendor", "Quoted value", "Status", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {sorted.map((b, i) => (
              <TableRow key={b.vendor} data-testid={`row-bid-${i}`}>
                <TableCell className="font-mono text-xs font-semibold">L{i + 1}</TableCell>
                <TableCell className="text-sm">{b.vendor}</TableCell>
                <TableCell className="font-mono text-sm">{inr(b.amount)}</TableCell>
                <TableCell><StatusBadge value={b.status} /></TableCell>
                <TableCell>{editable && b.status !== "Recommended" && <Button size="sm" variant="ghost" onClick={() => onUpdate({ recommended_vendor: b.vendor })} data-testid={`btn-recommend-${i}`}>Recommend</Button>}</TableCell>
              </TableRow>
            ))}
            {!sorted.length && <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500">No bids</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
};

const ACTION_LABELS = { Approval: "Approve award", "SAP Synced": "Sync to SAP" };
const actionLabel = (name) => ACTION_LABELS[name] || `Complete ${name}`;

const successMsg = (action, stage, data) => {
  if (action === "reject") return "Sent back · approver notified";
  if (stage === "WO Generated") return `Work Order ${data.wo_number} generated · added to Document Vault`;
  return `${stage} completed · next approver notified`;
};

const ActionBox = ({ c, stages, user, onDone }) => {
  const [note, setNote] = useState("");
  if (c.status === "completed") return <p className="text-sm text-emerald-700" data-testid="award-completed">Award completed · {c.sap_doc}</p>;
  const cur = stages[c.stage];
  const can = user.role === "admin" || user.role === cur.role;
  if (!can) return <p className="text-xs text-slate-500" data-testid="awaiting-role">Awaiting {cur.name} by {cur.role_label}</p>;
  const go = async (action) => {
    try {
      const { data } = await api.post(`/contract-awards/${c.id}/advance`, { action, note });
      toast.success(successMsg(action, cur.name, data));
      setNote("");
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const isWO = cur.name === "WO Generated";
  return (
    <div className="space-y-2">
      <Textarea rows={2} placeholder="Remarks (required to send back)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="input-award-note" />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => go("advance")} data-testid={isWO ? "btn-generate-wo" : "btn-advance-award"}>
          {isWO ? <><FileCheck2 className="mr-1.5 h-4 w-4" />Generate Work Order</> : <><Check className="mr-1.5 h-4 w-4" />{actionLabel(cur.name)}</>}
        </Button>
        {c.stage > 1 && <Button variant="outline" onClick={() => go("reject")} data-testid="btn-sendback-award"><Undo2 className="mr-1.5 h-4 w-4" />Send back</Button>}
      </div>
    </div>
  );
};

export default function ContractAward() {
  const { user } = useAuth();
  const [awards, setAwards] = useState([]);
  const [stages, setStages] = useState([]);
  const [sel, setSel] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const load = useCallback(() => api.get("/contract-awards").then((r) => { setAwards(r.data); setSel((s) => s || r.data[0]?.id); }), []);
  useEffect(() => { api.get("/contract-awards/meta").then((r) => setStages(r.data.stages)); load(); }, [load]);
  const c = awards.find((a) => a.id === sel);
  const replace = (x) => setAwards((a) => a.map((y) => (y.id === x.id ? x : y)));
  const editable = canWrite(user, "contracts") && c?.status !== "completed" && c?.stage < 4;
  const update = async (body) => {
    try { replace((await api.put(`/contract-awards/${c.id}`, body)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const canCreate = ["admin", "contracts", "site_qs"].includes(user.role);

  return (
    <div>
      <PageHeader dept="Contracts" title="Contract Award & Approval Workflow" subtitle="Tender → comparative statement → negotiation → approval → Work Order → SAP">
        {awards.length > 0 && (
          <Select value={sel || undefined} onValueChange={setSel}>
            <SelectTrigger className="w-60" data-testid="select-award"><SelectValue /></SelectTrigger>
            <SelectContent>{awards.map((a) => <SelectItem key={a.id} value={a.id}>{a.ref_no} · {a.site}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {canCreate && <Button onClick={() => setNewOpen(true)} data-testid="btn-new-award"><Plus className="mr-1.5 h-4 w-4" />New award</Button>}
      </PageHeader>
      {c && stages.length > 0 && (
        <div className="space-y-6">
          <Panel testId="award-stages">
            <div className="p-4 sm:p-6">
              <div className="mb-6 flex flex-wrap items-baseline gap-3">
                <span className="font-heading text-2xl font-bold" data-testid="award-title">{c.ref_no} {c.site}</span>
                <span className="text-sm text-slate-500">{c.package}</span>
                {c.wo_number && <span className="rounded bg-emerald-50 px-2 py-0.5 font-mono text-xs text-emerald-700" data-testid="wo-number">Work Order {c.wo_number}</span>}
                <div className="ml-auto"><PrintButton path={`/print/wo/${c.id}`} label={c.wo_number ? "Print Work Order" : "Print draft WO"} testId="btn-print-wo" /></div>
              </div>
              <div className="mb-6"><DetailGrid testId="award-details" items={[["Contract type", c.contract_type], ["Start", c.start_date && fmtDate(c.start_date)], ["Completion", c.completion_date && fmtDate(c.completion_date)], ["Payment terms", c.payment_terms], ["Mobilisation adv.", c.mobilisation_advance_pct != null ? `${c.mobilisation_advance_pct}%` : ""], ["Retention", c.retention_pct != null ? `${c.retention_pct}%` : ""], ["DLP", c.defect_liability_months != null ? `${c.defect_liability_months} months` : ""], ["Scope", c.scope]]} /></div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Approval stages</p>
              <HorizontalStepper steps={buildSteps(stages, c, c.status === "completed")} />
            </div>
          </Panel>
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="min-w-0 lg:col-span-7"><CSTable c={c} editable={editable} onUpdate={update} /></div>
            <div className="min-w-0 space-y-6 lg:col-span-5">
              <Panel title="Document checklist" testId="award-checklist">
                <div className="space-y-3 p-4">
                  {CHECKS.map(([k, l]) => (
                    <label key={k} className="flex items-start gap-2 text-sm">
                      <Checkbox className="mt-0.5" checked={!!c.checklist[k]} disabled={!canWrite(user, "contracts") || c.status === "completed" || !!c.wo_number}
                        onCheckedChange={(v) => update({ checklist: { ...c.checklist, [k]: !!v } })} data-testid={`check-${k}`} />{l}
                    </label>
                  ))}
                </div>
              </Panel>
              <Panel title="Action"><div className="p-4"><ActionBox c={c} stages={stages} user={user} onDone={replace} /></div></Panel>
            </div>
          </div>
        </div>
      )}
      <NewAwardDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(a) => { setAwards((x) => [a, ...x]); setSel(a.id); }} />
    </div>
  );
}
